"use client"

import { useStoreCache } from "@/app/store/slices/cache/hook"
import { Avatar, AvatarProps } from "@heroui/avatar"
import { Image, ImageProps } from "@heroui/image"
import { User, UserProps } from "@heroui/user"
import { useEffect, useState, memo } from "react"
import { AiOutlinePicture } from "react-icons/ai"

const endpoint = process.env.NEXT_PUBLIC_MINIO_ENDPOINT || "http://localhost:9000"
const bucket = process.env.NEXT_PUBLIC_MINIO_BUCKET_NAME || ""

const useImageLoader = (src?: string) => {
  const [url, setUrl] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const { get, push } = useStoreCache()

  useEffect(() => {
    if (!src) {
      setIsLoading(false)
      return
    }
    const cachedPath = get(src)
    if (cachedPath) {
      setUrl(cachedPath)
      setIsLoading(false)
      return
    }

    let finalUrl = ""

    if (src.startsWith("http")) {
      finalUrl = src
    }
    else if (src.startsWith("blob:http")) {
      finalUrl = src
    }
    else {
      const cleanSrc = src.startsWith("/") ? src.slice(1) : src
      finalUrl = `${endpoint}/${bucket}/${cleanSrc}`
    }

    push(src, finalUrl)
    setUrl(finalUrl)
    setIsLoading(false)
  }, [src, get, push])

  return { url, isLoading }
}

export const AvatarImage = memo((props: AvatarProps) => {
  const { src, imgProps, ...rest } = props
  const { url } = useImageLoader(src)

  return <Avatar {...rest} src={url} imgProps={{ ...imgProps, crossOrigin: "anonymous" }} />
}, (prev, next) => (
  prev.src === next.src &&
  prev.size === next.size &&
  prev.className === next.className &&
  prev.isBordered === next.isBordered
))

export const UserImage = memo((props: UserProps) => {
  const { avatarProps, ...rest } = props
  const { url } = useImageLoader(avatarProps?.src)

  return (
    <User
      {...rest}
      avatarProps={{ ...avatarProps, src: url, imgProps: { ...avatarProps?.imgProps, crossOrigin: "anonymous" } }}
    />
  )
}, (prev, next) => (
  prev.name === next.name &&
  prev.description === next.description &&
  prev.avatarProps?.src === next.avatarProps?.src &&
  prev.avatarProps?.size === next.avatarProps?.size
))

export const AsyncImage = memo((props: ImageProps) => {
  const { src, fallbackSrc, ...rest } = props
  const { url, isLoading } = useImageLoader(src)

  return (
    <Image
      {...rest}
      src={url}
      crossOrigin="anonymous"
      isLoading={isLoading || props.isLoading}
      fallbackSrc={fallbackSrc || <AiOutlinePicture size={40} />}
    />
  )
}, (prev, next) => (
  prev.src === next.src &&
  prev.className === next.className &&
  prev.width === next.width &&
  prev.isLoading === next.isLoading
))